import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const ReturnEditForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { 
    getReturnById, 
    updateReturn, 
    getProducts, 
    getCustomers,
    getSuppliers,
    fetchAllData 
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [returnType, setReturnType] = useState('customer');
  
  // Helper to consistently render an entity name
  const getEntityName = (ent) => {
    if (!ent) return '';
    const full = `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    if (full) return full;
    if (ent.name && ent.name.trim() !== '') return ent.name;
    if (ent.company && ent.company.trim() !== '') return ent.company;
    return String(ent.id || '');
  };
  
  const [returnData, setReturnData] = useState({
    returnNumber: '',
    date: new Date().toISOString().split('T')[0],
    entityId: '',
    entityName: '',
    entityEmail: '',
    entityPhone: '',
    reason: 'defective',
    status: 'pending',
    refundMethod: 'credit',
    notes: '',
    items: [],
    type: 'customer'
  });
  
  // Load return data
  useEffect(() => {
    const loadReturnData = async () => {
      try {
        // Try to get return data from location state first
        let returnItem = location.state?.returnItem;
        
        // If not available in state, fetch it using the ID
        if (!returnItem) {
          returnItem = getReturnById(id);
        }
        
        if (!returnItem) {
          toast.error('Retour introuvable');
          navigate('/returns');
          return;
        }
        
        // Set return type
        setReturnType(returnItem.type || 'customer');
        
        // Set return data
        setReturnData({
          ...returnItem,
          entityId: returnItem.entityId || '',
          entityName: returnItem.entityName || '',
          entityEmail: returnItem.entityEmail || '',
          entityPhone: returnItem.entityPhone || '',
          date: returnItem.date || new Date().toISOString().split('T')[0],
          reason: returnItem.reason || 'defective',
          status: returnItem.status || 'pending',
          refundMethod: returnItem.refundMethod || 'credit',
          notes: returnItem.notes || '',
        });
        
        // Set selected products
        if (Array.isArray(returnItem.items)) {
          setSelectedProducts(returnItem.items.map(item => ({
            id: item.id,
            name: item.name,
            code: item.sku || item.id,
            price: parseFloat(item.price) || 0,
            quantity: parseInt(item.quantity) || 1,
            total: parseFloat(item.total) || parseFloat(item.price) || 0
          })));
        }
      } catch (error) {
        console.error('Error loading return data:', error);
        toast.error('Erreur lors du chargement des données');
      }
    };
    
    loadReturnData();
  }, [id, location.state, getReturnById, navigate]);
  
  // Load products and entities
  useEffect(() => {
    const loadEntities = () => {
      // Get products
      const loadedProducts = getProducts();
      setProducts(loadedProducts.map(product => ({
        id: product.id,
        code: product.sku || product.id,
        name: product.name,
        price: parseFloat(product.price) || 0
      })));
      
      // Get entities based on return type
      if (returnType === 'customer') {
        const customers = getCustomers() || [];
        setEntities(customers.map((customer) => ({ 
          id: customer.id, 
          name: getEntityName(customer),
          email: customer.email,
          phone: customer.phone
        })));
      } else {
        const suppliers = getSuppliers() || [];
        setEntities(suppliers.map((supplier) => ({ 
          id: supplier.id, 
          name: getEntityName(supplier),
          email: supplier.email,
          phone: supplier.phone
        })));
      }
    };
    
    loadEntities();
  }, [returnType, getProducts, getCustomers, getSuppliers]);
  
  const reasonOptions = returnType === 'customer' 
    ? [
        { value: 'defective', label: 'Défectueux' },
        { value: 'wrong_size', label: 'Mauvaise taille' },
        { value: 'wrong_item', label: 'Mauvais produit' },
        { value: 'damaged', label: 'Endommagé' },
        { value: 'customer_choice', label: 'Choix du client' }
      ]
    : [
        { value: 'defective', label: 'Défectueux' },
        { value: 'wrong_item', label: 'Mauvais produit' },
        { value: 'damaged', label: 'Endommagé' },
        { value: 'expired', label: 'Expiré' },
        { value: 'other', label: 'Autre' }
      ];
  
  const refundMethodOptions = returnType === 'customer'
    ? [
        { value: 'credit', label: 'Avoir client' },
        { value: 'exchange', label: 'Échange' },
        { value: 'refund', label: 'Remboursement' }
      ]
    : [
        { value: 'credit', label: 'Avoir fournisseur' },
        { value: 'exchange', label: 'Échange' },
        { value: 'refund', label: 'Remboursement' }
      ];
  
  const statusOptions = [
    { value: 'pending', label: 'En attente' },
    { value: 'completed', label: 'Accepté' },
    { value: 'rejected', label: 'Refusé' }
  ];
  
  const handleAddProduct = (product) => {
    // Check if product already exists in selected products
    if (selectedProducts.some(p => p.id === product.id)) {
      toast.error('Ce produit est déjà dans la liste');
      return;
    }
    
    setSelectedProducts([...selectedProducts, {
      id: product.id,
      name: product.name,
      code: product.code,
      price: product.price,
      quantity: 1,
      total: product.price
    }]);
    setShowProductSearch(false);
    setSearchQuery('');
  };
  
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };
  
  const handleQuantityChange = (productId, quantity) => {
    const newQuantity = Math.max(1, parseInt(quantity) || 1);
    setSelectedProducts(selectedProducts.map(p => 
      p.id === productId 
        ? { ...p, quantity: newQuantity, total: p.price * newQuantity } 
        : p
    ));
  };
  
  const calculateTotal = () => {
    return selectedProducts.reduce((sum, product) => sum + product.total, 0);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (returnData.entityId === '') {
      toast.error(`Veuillez sélectionner un ${returnType === 'customer' ? 'client' : 'fournisseur'}`);
      return;
    }
    
    if (selectedProducts.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare return data for update
      const selectedEntity = entities.find(entity => entity.id === returnData.entityId);
      const returnToUpdate = {
        ...returnData,
        type: returnType,
        items: selectedProducts,
        totalAmount: calculateTotal(),
        entityName: selectedEntity?.name || returnData.entityName,
        entityEmail: selectedEntity?.email || returnData.entityEmail,
        entityPhone: selectedEntity?.phone || returnData.entityPhone
      };
      
      // Update the return
      await updateReturn(id, returnToUpdate);
      
      // Refresh data
      await fetchAllData();
      
      toast.success('Retour mis à jour avec succès');
      navigate('/returns');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du retour:', error);
      toast.error('Erreur lors de la mise à jour du retour');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/returns')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Modifier le Retour
            </h1>
            <p className="text-white/60 mt-1">
              Modifiez les détails du retour {returnData.returnNumber}
            </p>
          </div>
        </div>
      </div>
      
      {/* Form Container */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Numéro de retour */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  N° de Retour
                </label>
                <input
                  type="text"
                  value={returnData.returnNumber}
                  readOnly
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Date du retour
                </label>
                <input
                  type="date"
                  value={returnData.date}
                  onChange={(e) => setReturnData({ ...returnData, date: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Client/Fournisseur */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  {returnType === 'customer' ? 'Client' : 'Fournisseur'}
                </label>
                <select
                  value={returnData.entityId}
                  onChange={(e) => {
                    const selectedEntity = entities.find(entity => entity.id === e.target.value);
                    if (selectedEntity) {
                      setReturnData({
                        ...returnData,
                        entityId: e.target.value,
                        entityName: selectedEntity.name,
                        entityEmail: selectedEntity.email || '',
                        entityPhone: selectedEntity.phone || ''
                      });
                    } else {
                      setReturnData({
                        ...returnData,
                        entityId: e.target.value,
                        entityName: '',
                        entityEmail: '',
                        entityPhone: ''
                      });
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Sélectionner</option>
                  {entities.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Raison du retour */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Raison du retour
                </label>
                <select
                  value={returnData.reason}
                  onChange={(e) => setReturnData({ ...returnData, reason: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {reasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-6">
              {/* Statut */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Statut
                </label>
                <select
                  value={returnData.status}
                  onChange={(e) => setReturnData({ ...returnData, status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Méthode de remboursement */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Méthode de {returnType === 'customer' ? 'remboursement' : 'compensation'}
                </label>
                <select
                  value={returnData.refundMethod}
                  onChange={(e) => setReturnData({ ...returnData, refundMethod: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {refundMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Notes / Commentaires
                </label>
                <textarea
                  value={returnData.notes}
                  onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Ajoutez des notes concernant ce retour..."
                ></textarea>
              </div>
            </div>
          </div>
          
          {/* Divider */}
          <div className="my-8 border-t border-white/10"></div>
          
          {/* Products Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Articles retournés</h3>
              <button
                type="button"
                onClick={() => setShowProductSearch(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Ajouter un article</span>
              </button>
            </div>
            
            {/* Product Search Modal */}
            {showProductSearch && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 rounded-2xl w-full max-w-2xl p-6 border border-white/20">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">Sélectionner un produit</h3>
                    <button
                      onClick={() => setShowProductSearch(false)}
                      className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder="Rechercher un produit..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-2 text-left text-white">Nom</th>
                          <th className="px-4 py-2 text-left text-white">Code</th>
                          <th className="px-4 py-2 text-right text-white">Prix</th>
                          <th className="px-4 py-2 text-right text-white">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/10">
                        {products
                          .filter(product => 
                            product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            product.code.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map(product => (
                            <tr key={product.id} className="hover:bg-white/5">
                              <td className="px-4 py-2 text-white">{product.name}</td>
                              <td className="px-4 py-2 text-white/70">{product.code}</td>
                              <td className="px-4 py-2 text-right text-white">{product.price.toLocaleString()} DA</td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  onClick={() => handleAddProduct(product)}
                                  className="px-3 py-1 bg-blue-600/30 text-blue-400 rounded hover:bg-blue-600/50 transition-colors"
                                >
                                  Ajouter
                                </button>
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* Selected Products Table */}
            {selectedProducts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-2 text-left text-white">Produit</th>
                      <th className="px-4 py-2 text-left text-white">Code</th>
                      <th className="px-4 py-2 text-right text-white">Prix</th>
                      <th className="px-4 py-2 text-right text-white">Quantité</th>
                      <th className="px-4 py-2 text-right text-white">Total</th>
                      <th className="px-4 py-2 text-right text-white">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {selectedProducts.map(product => (
                      <tr key={product.id} className="hover:bg-white/5">
                        <td className="px-4 py-2 text-white">{product.name}</td>
                        <td className="px-4 py-2 text-white/70">{product.code}</td>
                        <td className="px-4 py-2 text-right text-white">{product.price.toLocaleString()} DA</td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            min="1"
                            value={product.quantity}
                            onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                            className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-right text-white">{product.total.toLocaleString()} DA</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => handleRemoveProduct(product.id)}
                            className="p-1 bg-red-600/30 text-red-400 rounded hover:bg-red-600/50 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-white/5">
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-right text-white font-semibold">Total</td>
                      <td className="px-4 py-3 text-right text-white font-semibold">{calculateTotal().toLocaleString()} DA</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-white/5 rounded-xl p-8 text-center">
                <p className="text-white/60">Aucun produit sélectionné. Cliquez sur "Ajouter un article" pour commencer.</p>
              </div>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-xl font-semibold hover:from-red-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <span>Mise à jour en cours...</span>
                </>
              ) : (
                <span>Mettre à jour le retour</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ReturnEditForm;